
import { Watch, Vue, Prop } from "vue-property-decorator";
import Component from "vue-class-component";
import Passenger, { PassengerState } from "../models/Passenger";
import Destination from "../models/Destination";
import vuetifyToast from "vuetify-toast";
import Helpers from "../../../util/Helpers";
import "./PassengerList.scss";
import { Group } from "@/components/Passenger/models/Group";
import GroupPicker from "./GroupPicker.vue";
import { PassengerEditor } from "@/components/Passenger/components/PassengerEditor.vue";
import { PassengerDialog } from "@/components/Passenger/components/PassengerDialog.vue";
import { RolePermission } from "@/models/Role";
import PassengerDestination from "@/components/Passenger/models/PassengerDestination";
import Routes from "../../../Routes";
import User from "@/models/User";
import { warn } from "console";
import Trip from "../models/Trip";
import PassengerListRow from "./PassengerListRow.vue";

@Component({
  components: {
    GroupPicker,
    PassengerListRow
  }
})
export class PassengerList extends Vue {
  public static NEW_GROUP_CREATOR_ITEM: string = "- New group -";

  @Prop({ default: null })
  destination: Destination;

  @Prop({ default: f => [] })
  hiddenPassengers: Passenger[];
  @Watch("hiddenPassengers")
  async hiddenPassengersChanged(){
    await this.getDataFromApi();
  }

  @Prop({ default: false })
  pickMode: boolean;

  @Prop({ default: null })
  title: string;

  @Prop({ default: false })
  hideShowOnlyActiveToggle: boolean;

  @Prop({ default: false })
  hideCloseButton: boolean;

  @Prop({ default: false })
  hideSubmitButton: boolean;

  @Prop({ default: false })
  hideCreateNewButton: boolean;

  @Prop({ default: () => []})
  extraHeaders: any[];

  @Prop({ default: () => []})
  allTrips: Trip[];

  @Prop({ default: false })
  onlyShowPassengersWithTrips: boolean;

  pickedPassengersCount: number = 0;

  pagination: any = {
    sortBy: "name",
    descending: false,
    rowsPerPage: 20
  };
  @Watch("pagination", {deep: true})
  async paginationChanged(){
    await this.getDataFromApi();
  }

  async getDataFromApi(){
    await this.$store.dispatch(
      'passenger/getPaginatedPassengers',
      { 
        params: 
        { 
          destinationIds: [this.routeDestinationId], 
          index: (this.pagination.page -1) * this.pagination.rowsPerPage, 
          count: this.pagination.rowsPerPage,
          sortBy: this.pagination.sortBy,
          descending: this.pagination.descending,
          onlyActive: this.showOnlyActivePassengers,
          nameOrYearGradeSearch: this.search,
          excludeIds: this.hiddenPassengers.map(p => p.id)
        }
      }
    );
  }

  searchLoading: boolean = false;
  search: string = "";
  @Watch("search")
  async searchChanged(){
    this.searchLoading = true;
    await this.getDataFromApi();
    this.searchLoading = false;
  }

  get defaultHeaders(): any[] {
    return [
      {
        i18n: "passenger.prop.name.label",
        text: "Name",
        value: "name",
        sortable: true,
        align: "left"
      },
      {
        i18n: "passenger.prop.homeAddress.label",
        text: "Home address",
        value: "homeAddress",
        sortable: true,
        align: "left"
      },
      {
        i18n: "passenger.prop.phoneNumbers.label",
        text: "Phone numbers",
        value: "phone",
        sortable: true,
        align: "left"
      },
      {
        i18n: "passenger.prop.tags.label",
        text: "Tags",
        value: "tags",
        sortable: true,
        align: "left"
      },
      {
        i18n: "passenger.prop.phoneNumbers.label",
        text: "Phone numbers",
        value: "phone",
        sortable: true,
        align: "left",
        showInPickMode: false,
        hideIfEmpty: true,
        hide: true
      },
      {
        i18n: "passenger.prop.homeAddress.label",
        text: "Home address",
        value: "homeAddress",
        sortable: true,
        align: "left",
        hide: true
      },
      {
        i18n: "passenger.prop.pickupAddress.label",
        text: "Pickup address",
        value: "pickupAddress",
        sortable: true,
        align: "left",
        hideIfEmpty: true,
        hide: true
      }
    ]
  }


  get allHeaders(): any[] {
    let headers = this.defaultHeaders.concat(this.extraHeaders);
    headers.push(
      {
        i18n: "passenger.list.header.actions",
        text: "Actions",
        sortable: false,
        showInPickMode: false,
        hide: !this.userCanSeeActionsColumn
      }
    );
    return headers;
  }

  get showTripsColumn(){
    return this.headers.some(h => h.value === "activeTripsCount");
  }

  @Prop({ default: false })
  show: boolean;

  @Watch("show")
  onShowChange(val, newVal) {
    this.updatePickedPassengersCount();
  }

  groupNamesToFilterIn: string[] = [];
  groupNamesToAssign: string[] = [];
  groupNamesToAssignForSinglePassenger: string[] = [];
  singlePassengerToAddGroupsFor: Passenger = null;
  savingPassengerGroups: boolean = false;
  showGroupAssignDialog: boolean = false;
  
  ready: boolean = false;

  @Watch("ready")
  onReadyChange(val, newVal) {
    this.updatePickedPassengersCount();
  }

  searchDebounceTimeout: any = null;

  constructor() {
    super();
  }

  passengerGroupNames(passenger: Passenger): string[] {
    const groupsString = passenger.groups ? passenger.groups : "";
    return groupsString.split(",").map(g => g.trim());
  }

  get helpers() {
    return Helpers;
  }

  get datatable() {
    // console.log("PassengerList", "get datatable()");
    return this.$refs.datatable as any;
  }

  get headers() {
    const headers = this.allHeaders.filter(
      h =>
        !h.hide &&
        (!this.pickMode || (h.showInPickMode !== false && this.pickMode)) &&
        (!h.value ||
          !h.hideIfEmpty ||
          this.destinationPassengers.find(p => p[h.value]))
      //  || this.destinationPassengers.find(p => p[h.value])
    );
    return headers;
  }

  get filteredItems(): Passenger[] {
    return this.filteredPassengers;
  }

  get showOnlyActivePassengers(): boolean {
    return this.$store.state.passenger.showOnlyActivePassengers;
  }
  @Watch("showOnlyActivePassengers")
  showOnlyActivePassengersChanged(){
    this.getDataFromApi();
  }

  set showOnlyActivePassengers(value: boolean) {
    this.$store.commit("passenger/changeShowActivePassengers", value);
  }

  get filteredPassengers(): Passenger[] {
    let passengers = this.passengers;
    if (this.onlyShowPassengersWithTrips){
      const tripPassengerIds = this.allTrips.map(t => t.passengerId);
      const uniqueTripPassengerIds = [...new Set(tripPassengerIds)];

      passengers = passengers.filter(p => uniqueTripPassengerIds.includes(p.id));
    }

    // Filtering is now done on server-side, disable client side filtering
    // if (this.showOnlyActivePassengers) {
    //   passengers = passengers.filter(p => !p.inactive)
    // }

    // passengers = this.search
    //   ? passengers.filter(
    //       p =>
    //         p.nameMatchesSearch(this.search) ||
    //         p.yearGradeMatchesSearch(this.search)
    //     )
    //   : passengers;

    return passengers;
  }

  get pickedFilteredItems(): Passenger[] {
    return this.filteredItems.filter((p: Passenger) => p.picked);
  }

  get allFilteredItemsSelected(): boolean {
    return this.filteredItems.every((p: Passenger) => p.picked);
  }

  set allFilteredItemsSelected(value) {
    this.onToggleAll(value);
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get destinations(): Destination[] {
    return this.$store.state.destination.destinations;
  }

  get selectedDestination(): Destination | undefined {
    return this.destinations.find(d => d.id === this.routeDestinationId);
  }

  get storePassengers(): Passenger[] {
    return this.$store.state.passenger.paginatedPassengers;
  }

  get isLoading(): boolean {
    return (
      this.$store.state.passenger.pending.paginatedPassengers ||
      this.$store.state.destination.pending.destinations
    );
  }

  get totalPassengers(): number {
    return this.$store.state.passenger.paginatedPassengersTotal;
  }

  get destinationPassengers(): Passenger[] {
    return this.selectedDestination
      ? this.storePassengers.filter((p: Passenger) =>
          p.passengerDestinations.some(
            pd => pd.destinationId === this.selectedDestination.id
          )
        )
      : [];
  }

  get passengers(): Passenger[] {
    let passengers = this.destinationPassengers.filter((p: Passenger) => {
      if (
        !this.groupNamesToFilterIn ||
        this.groupNamesToFilterIn.length === 0
      ) {
        return true;
      }

      if (p.groups) {
        const passengerGroupNames = p.groups.split(",").map(g => g.trim());
        return passengerGroupNames.some(g =>
          this.groupNamesToFilterIn.includes(g)
        );
      }
      return false;
    });

    passengers = passengers.filter(
      (p: Passenger) => !this.hiddenPassengers.includes(p)
    );
    return passengers;
  }

  set passengers(value) {
    // console.log("NO-OP: passengers set:", value);
  }

  get destinationGroups(): Group[] {
    // TODO: Get the destination passenger groups from API, restService.passengers.getPassengerAvailableDestinationGroups
    const groups = [];

    this.destinationPassengers.forEach((p: Passenger) => {
      if (p.groups) {
        p.groups.split(",").forEach(g => {
          groups.push({ name: g.trim() });
        });
      }
    });
    return groups;
  }

  get assignableGroups(): Group[] {
    return Group.getUniqueGroups(
      this.destinationGroups.concat(
        this.groupNamesToAssign.map(gn => new Group(gn))
      )
    );
  }

  activeTripsForPassenger(id: number) {
    return this.allTrips
      .filter((t) => t.passengerId === id)
      .filter((t) => !t.cancelledAt).length;
  }

  get me(): User {
    return this.$store.state.user.me;
  }

  get userCanEditGroups(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.UpdatePassengerGroups
        ])
      : false;
  }

  get userCanEditPassengers(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.UpdatePassengers
        ])
      : false;
  }

  get userCanDeletePassengers(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.DeletePassengers
        ])
      : false;
  }

  get userCanSeeActionsColumn(): boolean {
    return this.userCanEditPassengers && this.userCanDeletePassengers;
  }

  get userCanCreatePassengers(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.CreatePassengers
        ])
      : false;
  }

  passengerHasAddableGroups(passenger: Passenger): boolean {
    if (this.groupNamesToAssign.length === 0) {
      return false;
    }
    const passengerGroupNames = passenger.groups.split(",").map(g => g.trim());
    return !this.groupNamesToAssign.every(gn =>
      passengerGroupNames.includes(gn)
    );
  }

  mounted() {
    setTimeout(() => {
      this.ready = true;
    }, 500);
  }

  async onEditPassengerClick(passenger: Passenger) {
      this.$router.push({
        name: Routes.editPassengerRoute.name,
        params: {
          passengerId: passenger.id.toString(),
        }
      });
  }

  async onDeletePassengerClick(passenger: Passenger) {
    if (
      confirm(
        this.$i18n.translate(
          "passenger.delete.confirm_with_name",
          "Are you sure you want to delete {passengerName}?",
          {
            passengerName: passenger.name
          }
        )
      )
    ) {
      try {
        await this.$store.dispatch("passenger/deleteRequest", {
          params: { id: passenger.id }
        });
      } catch (err) {
        vuetifyToast.error(
          this.$i18n.translate(
            "passenger.delete.error.generic",
            "There was an error when deleting the passenger. Please try again later."
          )
        );
      }
    }
  }

  async savePassengerGroups(passenger) {
    try {
      await this.$store.dispatch("passenger/updatePassengerGroups", {
        params: { id: passenger.id },
        data: passenger
      });
    } catch (ex) {
      vuetifyToast.error(
        this.$i18n.translate(
          "passenger.save.error.generic",
          "There was an error saving the passenger. Please try again later."
        )
      );
    }
  }

  onAddGroupClick(passenger: Passenger) {
    this.groupNamesToAssignForSinglePassenger = [];
    this.singlePassengerToAddGroupsFor = passenger;
    this.showGroupAssignDialog = true;
  }

  async onGroupCellClick(passenger: Passenger) {
    if (passenger.inactive) {
      return;
    }
    if (this.pickMode) {
      this.toggleItemPicked({passenger: passenger, value: !passenger.picked});
    } else {
      const modified = passenger.addToGroupNames(this.groupNamesToAssign);
      if (!modified) {
        return;
      }
      await this.savePassengerGroups(passenger);
    }
  }

  async onGroupChipClick(passenger: Passenger, groupName: string) {
    return;
  }

  async onGroupChipInput(passenger: Passenger, groupName: string) {
    if (this.pickMode) {
      return;
    }
    const modified = passenger.removeFromGroupNames([groupName]);
    if (!modified) {
      return;
    }
    await this.savePassengerGroups(passenger);
  }

  onSearchInput(val) {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    this.searchDebounceTimeout = setTimeout(() => {
      this.search = val;
    }, 500);
  }

  updatePickedPassengersCount() {
    this.pickedPassengersCount = this.pickedFilteredItems.length;
  }

  toggleItemPicked({passenger, value}: {passenger: Passenger, value}) {
    if (passenger.inactive) {
      return;
    }
    passenger.picked = value;
    this.updatePickedPassengersCount();
    const pickedPassengers = this.pickedFilteredItems;
    this.$emit("change", pickedPassengers);
  }

  onToggleAll(value) {
    this.filteredItems.forEach((p: Passenger) => {
      p.picked = value;
    });
    this.updatePickedPassengersCount();
    const pickedPassengers = this.pickedFilteredItems;
    this.$emit("change", pickedPassengers);
  }

  onChangeSort(column) {
    // console.log("onChangeSort", "column:", column)
    if (this.pagination.sortBy === column) {
      this.pagination.descending = !this.pagination.descending;
    } else {
      this.pagination.sortBy = column;
      this.pagination.descending = false;
    }
  }

  onDatatableSort(items, index, isDesc) {
    items.sort((a: Passenger, b: Passenger) => {
      if (isDesc) {
        return b[index] < a[index] ? -1 : 1;
      } else {
        return a[index] < b[index] ? -1 : 1;
      }
    });
    return items;
  }

  onPassengerClick(event: Event, passenger: Passenger) {
    this.$root.$emit(PassengerDialog.EVENT_PASSENGER_DIALOG_SHOW, passenger);
  }

  onCreatePassengerClick() {
    this.$store.commit('passenger/resetSelectedPassenger');
    this.$router.push({ name: Routes.newPassengerRoute.name });
  }

  unpickAllPassengers() {
    this.pickedFilteredItems.forEach((p: Passenger) => {
      p.picked = false;
    });
  }
  onCloseClick() {
    this.pickedPassengersCount = 0;
    const pickedPassengers = this.pickedFilteredItems;
    this.unpickAllPassengers();
    this.$emit("close", pickedPassengers);
  }
  onSubmitClick() {
    const pickedPassengers = this.pickedFilteredItems;
    this.unpickAllPassengers();
    this.$emit("pickPassengers", pickedPassengers);
    this.pickedPassengersCount = 0;
  }
  onGroupFilterGroupPickerChange(selectedGroups: Group[]) {
    this.groupNamesToFilterIn = selectedGroups.map(g => g.name);
  }
  onGroupAssignGroupPickerChange(selectedGroups: Group[]) {
    this.groupNamesToAssign = selectedGroups.map(g => g.name);
  }
  onGroupAssignSingleGroupPickerChange(selectedGroups: Group[]) {
    this.groupNamesToAssignForSinglePassenger = selectedGroups.map(g => g.name);
  }
  onGroupAssignSingleCancelClick() {
    this.groupNamesToAssignForSinglePassenger = [];
    this.showGroupAssignDialog = false;
    this.singlePassengerToAddGroupsFor = null;
  }
  async onGroupAssignSingleOkClick() {
    const groupsModified: boolean = this.singlePassengerToAddGroupsFor.updateGroups(
      this.groupNamesToAssignForSinglePassenger
    );
    if (groupsModified) {
      this.savingPassengerGroups = true;
      await this.savePassengerGroups(this.singlePassengerToAddGroupsFor);
      this.savingPassengerGroups = false;
    }
    this.groupNamesToAssignForSinglePassenger = [];
    this.showGroupAssignDialog = false;
    this.singlePassengerToAddGroupsFor = null;
  }

  async created(){
   
  }

  beforeDestroy() {
    // TODO: Temporary workaround for selection sharing bug, handle this better
    this.storePassengers
      .filter((p: Passenger) => p.picked)
      .forEach((p: Passenger) => (p.picked = false));

    // Clear intervals and timeouts
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }
}

export default PassengerList;
